import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Save, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { humanizeKey, detectType } from "../lib/utils-fields";
import SearchableSelect from "./SearchableSelect";
import AracilarMultiField, { aracilarToIdList } from "./AracilarMultiField";
import { FK_FIELD_MAP, FIELD_CHOICES_MAP } from "../lib/choices";
import { crud, api, rayicIstihbarat, workflowValidate } from "../lib/api";
import ConfirmDialog from "./ConfirmDialog";
import DosyaIntelPanel from "./ai/DosyaIntelPanel";
import {
    inferKimlikTipi,
    kimlikNoLabel,
    kimlikNoMaxLength,
    kimlikNoPlaceholder,
    normalizeKimlikNo,
    validateMuvekkilKimlik,
} from "../lib/muvekkilKimlik";

/**
 * Generic Create/Edit form modal — FULL DYNAMIC SCHEMA EDITION
 *
 * Field source priority (highest → lowest):
 *   1. DECLARED (entities.js `fields`)         — explicit overrides
 *   2. DYNAMIC schema from Django (OPTIONS or /schema/?app=X&model=Y)
 *      → required flags, choices, FK info, max_length, help_text…
 *   3. DISCOVERED from initial / sampleRow      — auto-typed fallback
 *
 * Read-only / display-only fields are filtered out (they break POST/PATCH).
 *
 * Backend integration:
 *  - If `schema={app, model}` is provided, tries
 *      GET /v1/admin/_schema/?app=X&model=Y     (preferred, full Django introspection)
 *  - If that fails AND `resource` is provided, tries
 *      OPTIONS /v1/admin/<resource>/            (DRF default, returns actions.POST)
 *
 * Schema responses are cached per session (in-memory) to avoid repeat fetches.
 */

/* =================================================================
 *  CONSTANTS & UTILITIES
 * ================================================================= */

const FK_LABEL_ALIASES = {
    muvekkil: ["muvekkil_label", "muvekkil_ad_soyad"],
    avukat: ["avukat_label"],
    filo: ["filo_label"],
    filo_arac: ["filo_arac_label"],
    araci: ["araci_label"],
    sigorta_dosya: ["sigorta_dosyasi_label", "sigorta_dosya_label"],
    sigorta_dosyasi: ["sigorta_dosyasi_label", "sigorta_dosya_label"],
};

function fkLabelHint(initial, fieldName) {
    if (!initial) return "";
    const keys = [
        `${fieldName}_label`,
        `${fieldName}_display`,
        ...(FK_LABEL_ALIASES[fieldName] || []),
    ];
    for (const k of keys) {
        const v = initial[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") {
            return String(v);
        }
    }
    return "";
}

function fieldForRender(fld, values) {
    if (!fld?.kimlikLinked) return fld;
    const tip = values?.kimlik_tipi || "TC";
    return {
        ...fld,
        label: kimlikNoLabel(tip),
        placeholder: kimlikNoPlaceholder(tip),
        maxLength: kimlikNoMaxLength(tip),
        inputMode: "numeric",
    };
}

const GROUP_ORDER = [
    "Müvekkil Bilgileri",
    "Dosya Bilgileri",
    "Araç Bilgileri",
    "Sigorta Bilgileri",
    "Süre Takibi",
];

/** Select: API kodu veya *_display etiketinden option value bul */
function seedSelectValue(fld, initial) {
    const opts = fld.options || [];
    let raw = initial?.[fld.name];
    // 0 geçerli bir kusur oranı — boş sayma
    if (raw === null || raw === undefined || raw === "") {
        const display = initial?.[`${fld.name}_display`];
        if (display) {
            const byLabel = opts.find(
                (o) =>
                    String(o.label).toLowerCase() ===
                    String(display).toLowerCase(),
            );
            if (byLabel) return byLabel.value;
        }
        return "";
    }
    const hit = opts.find((o) => String(o.value) === String(raw));
    if (hit) return hit.value;
    const display = initial?.[`${fld.name}_display`];
    if (display) {
        const byLabel = opts.find(
            (o) =>
                String(o.label).toLowerCase() === String(display).toLowerCase(),
        );
        if (byLabel) return byLabel.value;
    }
    return raw;
}

const MULTI_FK_FIELDS = new Set(["aracilar"]);

function isMultiFkField(fld) {
    return fld?.multi === true || MULTI_FK_FIELDS.has(fld?.name);
}

/** FK select: id + kayıttaki etiket → anında doğru isim */
function resolveFkSelectValue(value, labelHint, { multi = false } = {}) {
    if (value === null || value === undefined || value === "") {
        return multi ? [] : null;
    }
    if (multi) {
        if (Array.isArray(value)) return value;
        if (typeof value === "object" && Array.isArray(value?.id)) {
            return value.id;
        }
        if (typeof value === "object" && value?.id !== undefined) {
            return [value.id];
        }
        if (typeof value === "number" || /^\d+$/.test(String(value))) {
            return [typeof value === "number" ? value : Number(String(value))];
        }
        return [];
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null;
    }
    if (typeof value === "object" && value.id !== undefined) return value;
    const hint = (labelHint || "").trim();
    if (hint) return { id: value, __label: hint };
    return value;
}

const SYSTEM_FIELDS = new Set([
    "id",
    "uuid",
    "created_at",
    "updated_at",
    "created",
    "modified",
    "created_by",
    "updated_by",
    "owner",
    "slug",
]);

/**
 * Patterns for fields that the API exposes for *display only* — typically
 * SerializerMethodField / nested read-only representations.  These must
 * never appear on Create or Edit forms because POST/PATCH would either
 * reject them or duplicate the real writable field.
 */
const READONLY_PATTERNS = [
    /_bilgisi$/i,
    /_ad_soyad$/i,
    /_adi$/i,
    /_display$/i,
    /_label$/i,
    /^toplam_/i,
    /^total_/i,
    /^count_/i,
    /_count$/i,
    /^display_/i,
    /^mail_/i,
    /^eksik_/i,
    /^hesaplanan_/i,
];

function isReadOnlyName(name) {
    return READONLY_PATTERNS.some((re) => re.test(name));
}

function normalizeInitialField(fld, initial) {
    let v = initial?.[fld.name];
    if (fld.type === "fk" && fld.fkSubmit !== "label") {
        if (isMultiFkField(fld)) {
            if (v == null || v === "") return [];
            const arr = Array.isArray(v) ? v : [v];
            return arr
                .map((item) =>
                    typeof item === "object" && item != null && !Array.isArray(item)
                        ? item.id ?? item
                        : item,
                )
                .filter((id) => id !== null && id !== undefined && id !== "");
        }
        if (typeof v === "object" && v != null && !Array.isArray(v)) {
            return v.id ?? v;
        }
    }
    return v;
}

function fkCompareKey(fld, v) {
    if (v == null || v === "") return isMultiFkField(fld) ? "" : "";
    if (isMultiFkField(fld)) {
        const arr = Array.isArray(v) ? v : [v];
        return arr
            .map((item) =>
                typeof item === "object" && item != null && !Array.isArray(item)
                    ? item.id ?? item
                    : item,
            )
            .filter((id) => id !== null && id !== undefined && id !== "")
            .map(String)
            .sort()
            .join(",");
    }
    if (typeof v === "object" && v != null && !Array.isArray(v)) {
        return String(v.id ?? v);
    }
    return String(v);
}

/** fkSubmit:"label" — karşılaştırma ve gönderim için metin etiket */
function resolveFkLabelValue(fld, bag, { fromInitial = false } = {}) {
    const name = fld.name;
    if (!fromInitial) {
        const explicit = bag?.[`${name}__label`];
        if (
            explicit !== undefined &&
            explicit !== null &&
            String(explicit).trim() !== ""
        ) {
            return String(explicit).trim();
        }
    }
    if (fromInitial) {
        const hint = fkLabelHint(bag, name);
        if (hint) return hint;
    }
    const raw = bag?.[name];
    if (raw === null || raw === undefined || raw === "") return "";
    if (typeof raw === "string" && !/^\d+$/.test(raw.trim())) return raw.trim();
    return "";
}

function sameFieldValue(fld, a, b, values = null, initial = null) {
    if (fld.type === "boolean") return !!a === !!b;
    if (fld.type === "fk" && fld.fkSubmit === "label" && values && initial) {
        return (
            resolveFkLabelValue(fld, values) ===
            resolveFkLabelValue(fld, initial, { fromInitial: true })
        );
    }
    if (fld.type === "fk") {
        if (isMultiFkField(fld)) {
            return fkCompareKey(fld, a) === fkCompareKey(fld, b);
        }
        const na =
            a == null || a === ""
                ? null
                : typeof a === "object"
                  ? a.id ?? a
                  : a;
        const nb =
            b == null || b === ""
                ? null
                : typeof b === "object"
                  ? b.id ?? b
                  : b;
        return String(na ?? "") === String(nb ?? "");
    }
    if (fld.type === "number") {
        const na = a === "" || a == null ? null : Number(a);
        const nb = b === "" || b == null ? null : Number(b);
        if (Number.isNaN(na) && Number.isNaN(nb)) return true;
        return na === nb;
    }
    return String(a ?? "") === String(b ?? "");
}

/**
 * Map a DRF schema field type to our internal field type.
 */
function mapDrfType(drfType, info = {}) {
    const t = String(drfType || "").toLowerCase();
    switch (t) {
        case "string":
        case "char":
        case "text":
            return info.max_length && info.max_length > 200 ? "textarea" : "text";
        case "integer":
        case "int":
        case "biginteger":
        case "smallinteger":
        case "decimal":
        case "float":
            return "number";
        case "boolean":
        case "nullbooleanfield":
            return "boolean";
        case "date":
            return "date";
        case "datetime":
            return "datetime";
        case "time":
            return "datetime";
        case "email":
            return "email";
        case "url":
            return "url";
        case "choice":
        case "multiplechoicefield":
            return "select";
        case "field":
        case "foreign key":
        case "foreignkey":
        case "primarykeyrelatedfield":
        case "relatedfield":
        case "related":
            return "fk";
        case "many":
        case "manytomany":
        case "manyrelatedfield":
            return "fk";
        default:
            // Unknown → safe fallback
            return "text";
    }
}

/**
 * Convert DRF OPTIONS response action metadata into our field schema.
 *
 * DRF returns shape like:
 *   {
 *     "muvekkil": { type, required, label, read_only, choices: [...] },
 *     ...
 *   }
 * Our internal field shape:
 *   { name, label, type, required?, options?, resource?, multi?, readOnly?, ... }
 */
function parseDrfActions(actions) {
    if (!actions || typeof actions !== "object") return [];
    const meta = actions.POST || actions.PUT || actions.PATCH || actions;
    if (!meta || typeof meta !== "object") return [];
    const out = [];
    for (const [name, info] of Object.entries(meta)) {
        if (!info || typeof info !== "object") continue;
        if (SYSTEM_FIELDS.has(name)) continue;
        if (isReadOnlyName(name)) continue;
        if (info.read_only) continue; // can't post these

        const fld = {
            name,
            label: info.label || humanizeKey(name),
            type: mapDrfType(info.type, info),
            required: !!info.required,
            placeholder: info.help_text || "",
        };

        // Choices → select options
        if (Array.isArray(info.choices) && info.choices.length > 0) {
            fld.type = "select";
            fld.options = info.choices.map((c) => ({
                value: c.value !== undefined ? c.value : c,
                label:
                    c.display_name ||
                    c.label ||
                    String(c.value !== undefined ? c.value : c),
            }));
        }

        // M2M relations
        if (
            info.type === "many" ||
            info.type === "multiple_choice" ||
            info.relation_type === "many_to_many"
        ) {
            fld.multi = true;
            fld.type = "fk";
        }

        // FK detection by name → resource map
        if (fld.type === "fk") {
            fld.resource =
                info.related_resource ||
                info.resource ||
                FK_FIELD_MAP[name] ||
                null;
        }

        // max_length → useful for text inputs
        if (info.max_length) fld.maxLength = info.max_length;

        out.push(fld);
    }
    return out;
}

/**
 * Convert our custom /_schema/ endpoint response into our field schema.
 * Expected shape (one of):
 *   { fields: [{ name, type, ... }] }
 *   { fields: { fieldName: {...} } }
 *   { actions: { POST: {...} } }   (same as DRF OPTIONS)
 */
function parseCustomSchema(data) {
    if (!data) return [];
    // DRF-style envelope
    if (data.actions) return parseDrfActions(data.actions);
    // Array of field objects
    if (Array.isArray(data.fields)) {
        return data.fields
            .filter((f) => {
                if (!f || !f.name) return false;
                if (SYSTEM_FIELDS.has(f.name)) return false;
                if (isReadOnlyName(f.name)) return false;
                if (f.read_only || f.readonly) return false;
                return true;
            })
            .map((f) => ({
                name: f.name,
                label: f.label || f.verbose_name || humanizeKey(f.name),
                type: mapDrfType(f.type, f),
                required: !!f.required,
                placeholder: f.help_text || f.placeholder || "",
                ...(f.choices && f.choices.length > 0
                    ? {
                          type: "select",
                          options: f.choices.map((c) => ({
                              value: c.value !== undefined ? c.value : c,
                              label:
                                  c.display_name ||
                                  c.label ||
                                  String(c.value !== undefined ? c.value : c),
                          })),
                      }
                    : {}),
                ...(f.relation_type === "many_to_many" || f.many === true
                    ? { multi: true, type: "fk" }
                    : {}),
                ...(f.relation_type ||
                f.relation === "fk" ||
                String(f.type || "").toLowerCase().includes("foreign")
                    ? {
                          type: "fk",
                          resource:
                              f.related_resource ||
                              f.resource ||
                              FK_FIELD_MAP[f.name] ||
                              null,
                      }
                    : {}),
            }));
    }
    // Object map { fieldName: {...} }
    if (data.fields && typeof data.fields === "object") {
        return parseDrfActions({ POST: data.fields });
    }
    return [];
}

/* =================================================================
 *  SCHEMA FETCH (with in-memory cache)
 * ================================================================= */

const _schemaCache = new Map();

function cacheKey({ resource, schema }) {
    if (schema?.app && schema?.model) return `s:${schema.app}/${schema.model}`;
    if (resource) return `r:${resource}`;
    return null;
}

async function fetchSchemaFor({ resource, schema }) {
    const key = cacheKey({ resource, schema });
    if (!key) return null;
    if (_schemaCache.has(key)) return _schemaCache.get(key);

    let result = null;

    // Strategy 1 — Django model schema (admin/schema/<app>/<model>/)
    if (schema?.app && schema?.model) {
        try {
            const r = await api.get(
                `/v1/admin/schema/${schema.app}/${schema.model}/`,
            );
            const fields = parseCustomSchema(r.data);
            if (fields.length > 0) result = fields;
        } catch (e) {
            // fall through to OPTIONS
        }
    }

    // Strategy 2 — DRF OPTIONS on the resource
    if (!result && resource) {
        try {
            const r = await api.options(`/v1/admin/${resource}/`);
            const fields = parseDrfActions(r.data?.actions);
            if (fields.length > 0) result = fields;
        } catch (e) {
            // OPTIONS may be locked down; we'll just have no dynamic fields
        }
    }

    _schemaCache.set(key, result || []);
    return result || [];
}

/* =================================================================
 *  FIELD DISCOVERY (from sample data — fallback)
 * ================================================================= */

function buildExtraField(name, sampleValue) {
    if (SYSTEM_FIELDS.has(name) || isReadOnlyName(name)) return null;

    if (
        Array.isArray(sampleValue) &&
        sampleValue.length > 0 &&
        typeof sampleValue[0] === "object"
    ) {
        return null;
    }

    const fkResource =
        FK_FIELD_MAP[name] ||
        (sampleValue &&
        typeof sampleValue === "object" &&
        !Array.isArray(sampleValue) &&
        sampleValue?.id !== undefined
            ? FK_FIELD_MAP[name]
            : null);
    if (fkResource) {
        return {
            name,
            label: humanizeKey(name),
            type: "fk",
            resource: fkResource,
            ...(Array.isArray(sampleValue) ? { multi: true } : {}),
        };
    }
    if (
        FIELD_CHOICES_MAP[name]
    ) {
        return {
            name,
            label: humanizeKey(name),
            type: "select",
            options: FIELD_CHOICES_MAP[name],
        };
    }
    if (
        typeof sampleValue === "string" &&
        (sampleValue.length > 80 ||
            /^(notlar|aciklama|mesaj|icerik|adres)$/i.test(name))
    ) {
        return {
            name,
            label: humanizeKey(name),
            type: "textarea",
        };
    }
    const t = detectType(name, sampleValue);
    if (t === "object") return null;
    return {
        name,
        label: humanizeKey(name),
        type: t === "object" ? "text" : t,
    };
}

/* =================================================================
 *  COMPONENT
 * ================================================================= */

export default function EntityForm({
    open,
    title,
    fields = [],
    initial,
    loading = false,
    onClose,
    onSubmit,
    extraSection = null,
    sampleRow = null,
    computedOverrides = null,
    onValuesChange = null,

    /* NEW — dynamic schema support */
    resource = null, // resource slug for DRF OPTIONS request
    schema = null, // { app, model } for /_schema/?app=X&model=Y
    formFieldsStrict = false,
}) {
    const isEdit = !!initial?.id;

    const [schemaFields, setSchemaFields] = useState(null);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [schemaError, setSchemaError] = useState(null);
    const fetchedKeyRef = useRef(null);

    /* ------- Fetch schema once per (resource, schema) pair ------- */
    useEffect(() => {
        if (!open) return;
        const key = cacheKey({ resource, schema });
        if (!key) return;
        if (fetchedKeyRef.current === key) return;
        fetchedKeyRef.current = key;

        let cancelled = false;
        setSchemaLoading(true);
        setSchemaError(null);

        fetchSchemaFor({ resource, schema })
            .then((flds) => {
                if (cancelled) return;
                setSchemaFields(flds);
            })
            .catch((e) => {
                if (cancelled) return;
                setSchemaError(e?.message || "Schema yüklenemedi");
                // eslint-disable-next-line no-console
                console.warn(`[EntityForm] schema fetch failed for ${key}`, e);
            })
            .finally(() => {
                if (!cancelled) setSchemaLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, resource, schema]);

    /* ------- Detect explicit groups in declared fields ------- */
    const groups = useMemo(() => {
        const explicit = fields.find((f) => f.__groups);
        if (explicit) return explicit.__groups;
        const grouped = fields.reduce((acc, f) => {
            if (!f.group) return acc;
            const g = acc.find((x) => x.title === f.group);
            if (g) g.fields.push(f);
            else acc.push({ title: f.group, fields: [f] });
            return acc;
        }, []);
        return grouped.length ? grouped : null;
    }, [fields]);

    /* ------- Merge declared + dynamic + discovered ------- */
    const allFields = useMemo(() => {
        // __groups içindeki alanlar formda gösterilir ama üst seviye fields'ta değildir;
        // submit/seed için mutlaka allFields'a dahil et.
        const groupedDeclared = (groups || []).flatMap((g) =>
            (g.fields || []).filter((f) => !f.hidden),
        );
        // Layer 1: declared (preserve order, groups, custom props)
        const declared = [
            ...fields.filter((f) => !f.hidden && !f.__groups),
            ...groupedDeclared.filter(
                (f) => !fields.some((x) => x.name === f.name && !x.__groups),
            ),
        ];
        const declaredMap = new Map(declared.map((f) => [f.name, f]));

        // Layer 2: dynamic schema from backend
        let merged = [...declared];
        if (Array.isArray(schemaFields) && schemaFields.length > 0) {
            // Update declared fields with schema metadata where applicable
            merged = merged.map((d) => {
                const s = schemaFields.find((sf) => sf.name === d.name);
                if (!s) return d;
                // Declared fields WIN on type/options/group/required where set,
                // schema fills the GAPS (label, choices, FK resource, etc.)
                return {
                    ...s,
                    ...d,
                    multi: d.multi ?? s.multi ?? MULTI_FK_FIELDS.has(d.name),
                    // Required flag: schema can mark required, declared can override
                    required: d.required ?? s.required,
                    // Choices: prefer declared if present, else use schema
                    options: d.options || s.options,
                    // FK resource: prefer declared, else schema
                    resource: d.resource || s.resource,
                };
            });
            if (!formFieldsStrict) {
                const schemaOnly = schemaFields.filter(
                    (sf) => !declaredMap.has(sf.name),
                );
                merged = [...merged, ...schemaOnly];
            }
        }

        if (!formFieldsStrict) {
            const mergedNames = new Set(merged.map((f) => f.name));
            const source = initial || sampleRow || null;
            if (source) {
                const extras = Object.keys(source)
                    .filter((k) => !mergedNames.has(k) && !SYSTEM_FIELDS.has(k))
                    .map((k) => buildExtraField(k, source[k]))
                    .filter(Boolean);
                merged = [...merged, ...extras];
            }
        }

        return merged;
    }, [fields, schemaFields, initial, sampleRow, formFieldsStrict, groups]);

    /* ------- Re-build groups using merged fields (so dynamic fields slot in) ------- */
    const renderedGroups = useMemo(() => {
        if (!groups) return null;
        const built = groups.map((g) => ({
            title: g.title,
            fields: g.fields.map((f) => {
                const m = allFields.find((x) => x.name === f.name);
                return m || f;
            }),
        }));
        const rank = (title) => {
            const i = GROUP_ORDER.indexOf(title);
            return i === -1 ? 999 : i;
        };
        return [...built].sort((a, b) => rank(a.title) - rank(b.title));
    }, [groups, allFields]);

    /* ------- Fields not in any group (extras to render at the bottom) ------- */
    const ungroupedFields = useMemo(() => {
        if (!groups) return allFields;
        const inGroup = new Set();
        for (const g of groups) {
            for (const f of g.fields) inGroup.add(f.name);
        }
        return allFields.filter((f) => !inGroup.has(f.name) && !f.group);
    }, [groups, allFields]);

    /* ------- Form state ------- */
    const [values, setValues] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [workflowConfirmOpen, setWorkflowConfirmOpen] = useState(false);
    const [workflowConfirmLines, setWorkflowConfirmLines] = useState([]);
    const workflowConfirmRef = useRef(null);
    const [autoFilling, setAutoFilling] = useState(false);
    const [rayicLoading, setRayicLoading] = useState(false);
    const lastAutoRayicKeyRef = useRef("");

    const isValidRayic = (value) => {
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
    };

    const parseKm = (raw) => {
        const n = parseInt(String(raw ?? "").replace(/\./g, ""), 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const formatRayicToastDesc = (res, marka, model, model_yili, km) => {
        const piyasaAdet = res.piyasa_ozet?.adet ?? res.dis_arastirma?.piyasa_adet ?? 0;
        const kmBand =
            res.km_ozet?.km_band
            || res.ic_kayit?.segment?.km_band
            || res.dis_arastirma?.km_band
            || null;
        const kmLine = km > 0
            ? `${Number(km).toLocaleString("tr-TR")} km${kmBand ? ` (${kmBand} bandı)` : ""}`
            : null;
        return [
            kmLine,
            res.model_arama_notu,
            res.marka_duzeltme_notu,
            `${res.marka_duzeltildi || marka} ${res.model_arama || model} (${model_yili}) — ${piyasaAdet} piyasa ilanı, KM-duyarlı medyan.`,
        ]
            .filter(Boolean)
            .join(" ");
    };

    // Manual AI Rayiç Lookup
    const handleRayicLookup = async () => {
        const { marka, model, model_yili, km } = values;
        const kmVal = parseKm(km);
        if (!marka || !model || !model_yili) {
            toast.error("Rayiç sorgulamak için Marka, Model ve Model Yılı alanlarını doldurmalısınız.");
            return;
        }
        if (!kmVal) {
            toast.error("Rayiç sorgulamak için KM bilgisini girmelisiniz.", {
                description: "Piyasa rayiç değeri km bandına göre hesaplanır; önce aracın kilometresini girin.",
            });
            return;
        }
        setRayicLoading(true);
        try {
            const params = {
                marka: String(marka).trim(),
                model: String(model).trim(),
                model_yili: parseInt(model_yili, 10),
                km: kmVal,
                derin: true, // Deep search for manual trigger
            };
            const res = await rayicIstihbarat.arastir(params);
            if (res?.marka_duzeltildi && res.marka_duzeltildi !== String(marka).trim()) {
                setValues((s) => ({ ...s, marka: res.marka_duzeltildi }));
            }
            const onerilen = Number(res?.onerilen_rayic);
            if (res && isValidRayic(onerilen)) {
                setValues((s) => ({ ...s, arac_rayic_degeri: onerilen }));
                lastAutoRayicKeyRef.current = `${marka}|${model}|${model_yili}|${kmVal}`;
                toast.success(`Araç Rayiç Değeri tespit edildi: ₺${onerilen.toLocaleString("tr-TR")}`, {
                    description: formatRayicToastDesc(res, marka, model, model_yili, kmVal),
                });
            } else {
                const hint = res?.model_arama_notu
                    || res?.marka_duzeltme_notu
                    || (String(marka).toUpperCase().includes("RENOULT")
                        ? "Marka yazımı hatalı olabilir: RENAULT deneyin."
                        : null);
                toast.error("Rayiç bedel tespit edilemedi.", {
                    description:
                        hint
                        || "Marka/model yazımını kontrol edin (ör. RENAULT, MEGANE). Piyasa verisi veya iç kayıt bulunamadı.",
                });
            }
        } catch (e) {
            toast.error("Rayiç sorgulama sırasında bir hata oluştu.");
            console.error(e);
        } finally {
            setRayicLoading(false);
        }
    };

    // Auto-fetch vehicle market value (rayiç) for sigorta-dosyalari — KM zorunlu
    useEffect(() => {
        if (resource !== "sigorta-dosyalari") return;
        const { marka, model, model_yili, km } = values;
        const kmVal = parseKm(km);
        if (
            !marka
            || !model
            || !model_yili
            || !kmVal
            || String(marka).trim().length < 2
            || String(model).trim().length < 2
        ) {
            return;
        }

        const signature = `${String(marka).trim()}|${String(model).trim()}|${model_yili}|${kmVal}`;
        const modelYiliInt = parseInt(model_yili, 10);
        if (isNaN(modelYiliInt) || modelYiliInt < 1970) return;

        const timer = setTimeout(async () => {
            try {
                const params = {
                    marka: String(marka).trim(),
                    model: String(model).trim(),
                    model_yili: modelYiliInt,
                    km: kmVal,
                    derin: false, // quick search for auto-fill
                };

                const res = await rayicIstihbarat.arastir(params);
                const onerilen = Number(res?.onerilen_rayic);
                if (res && isValidRayic(onerilen)) {
                    const kmChanged = lastAutoRayicKeyRef.current && lastAutoRayicKeyRef.current !== signature;
                    lastAutoRayicKeyRef.current = signature;
                    setValues((s) => {
                        const shouldUpdate = !isValidRayic(s.arac_rayic_degeri) || kmChanged;
                        if (shouldUpdate) {
                            toast.success(
                                kmChanged && isValidRayic(s.arac_rayic_degeri)
                                    ? `KM güncellendi — rayiç yeniden hesaplandı: ₺${onerilen.toLocaleString("tr-TR")}`
                                    : `Araç Rayiç Değeri otomatik hesaplandı: ₺${onerilen.toLocaleString("tr-TR")}`,
                                {
                                    description: formatRayicToastDesc(res, marka, model, model_yili, kmVal),
                                },
                            );
                            return { ...s, arac_rayic_degeri: onerilen };
                        }
                        return s;
                    });
                }
            } catch (e) {
                console.error("Rayiç otomatik doldurma hatası:", e);
            }
        }, 1200); // 1.2s debounce

        return () => clearTimeout(timer);
    }, [values.marka, values.model, values.model_yili, values.km, resource]);

    const seededRecordRef = useRef(null);

    function buildSeedFromInitial(sourceFields, sourceInitial) {
        const seed = {};
        for (const fld of sourceFields) {
            if (isReadOnlyName(fld.name) || fld.name.endsWith("_display")) continue;
            let v = sourceInitial?.[fld.name];
            if (
                (v === null || v === undefined || v === "") &&
                sourceInitial?.[`${fld.name}_display`] != null &&
                sourceInitial[`${fld.name}_display`] !== ""
            ) {
                v = sourceInitial[`${fld.name}_display`];
            }
            if (
                (v === null || v === undefined || v === "") &&
                sourceInitial?.[`${fld.name}_label`] != null &&
                sourceInitial[`${fld.name}_label`] !== "" &&
                !isMultiFkField(fld)
            ) {
                v = sourceInitial[`${fld.name}_label`];
            }
            if (fld.name === "kimlik_tipi") {
                seed[fld.name] =
                    sourceInitial?.kimlik_tipi ||
                    inferKimlikTipi(sourceInitial?.tc_kimlik_no) ||
                    "TC";
            } else if (fld.type === "boolean") seed[fld.name] = !!v;
            else if (fld.type === "fk") {
                const raw = sourceInitial?.[fld.name];
                if (fld.fkSubmit === "label") {
                    const hint = fkLabelHint(sourceInitial, fld.name);
                    if (hint) seed[`${fld.name}__label`] = hint;
                    if (raw === null || raw === undefined || raw === "") {
                        seed[fld.name] = null;
                    } else if (
                        typeof raw === "number" ||
                        /^\d+$/.test(String(raw))
                    ) {
                        seed[fld.name] =
                            typeof raw === "number"
                                ? raw
                                : Number(String(raw));
                    } else {
                        seed[fld.name] = raw;
                    }
                } else if (raw === null || raw === undefined || raw === "") {
                    seed[fld.name] = isMultiFkField(fld) ? [] : null;
                } else if (isMultiFkField(fld)) {
                    const arr = Array.isArray(raw) ? raw : [raw];
                    seed[fld.name] = arr
                        .map((item) =>
                            typeof item === "object" && item != null && !Array.isArray(item)
                                ? item.id ?? item
                                : item,
                        )
                        .filter((id) => id !== null && id !== undefined && id !== "");
                } else if (typeof raw === "object" && !Array.isArray(raw)) {
                    seed[fld.name] = raw?.id ?? raw;
                } else {
                    seed[fld.name] = raw;
                }
            } else if (fld.type === "select") {
                seed[fld.name] = seedSelectValue(fld, sourceInitial);
            } else if (v === null || v === undefined) seed[fld.name] = "";
            else if (Array.isArray(v)) seed[fld.name] = v;
            else if (typeof v === "object") seed[fld.name] = v?.id ?? "";
            else seed[fld.name] = v;
        }
        return seed;
    }

    useEffect(() => {
        if (!open) {
            seededRecordRef.current = null;
            return;
        }
        if (loading) return;
        const recordKey = initial?.id != null ? String(initial.id) : "create";
        const seed = buildSeedFromInitial(allFields, initial);

        if (seededRecordRef.current === recordKey) {
            setValues((prev) => {
                const next = { ...prev };
                let changed = false;
                for (const [key, val] of Object.entries(seed)) {
                    if (next[key] === undefined) {
                        next[key] = val;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
            return;
        }

        seededRecordRef.current = recordKey;
        setValues(seed);
        setErr(null);
    }, [open, loading, initial?.id, allFields, initial]);

    useEffect(() => {
        if (!open || !computedOverrides) return;
        setValues((s) => ({ ...s, ...computedOverrides }));
    }, [open, computedOverrides]);

    useEffect(() => {
        if (!open || typeof onValuesChange !== "function") return;
        onValuesChange(values);
    }, [open, values, onValuesChange]);

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    function update(name, val) {
        setValues((s) => ({ ...s, [name]: val }));
    }

    /* ------- Auto-fill from FK source record ------- */
    async function handleAutoFill(fld, id) {
        const cfg = fld?.autoFillFrom;
        if (!cfg || !id || Array.isArray(id)) return;
        const realId = typeof id === "object" ? id?.id : id;
        if (!realId) return;
        setAutoFilling(true);
        try {
            const rec = await crud.retrieve(cfg.resource, realId);
            setValues((s) => {
                const next = { ...s };
                let filled = 0;
                for (const k of cfg.fields || []) {
                    const v = rec?.[k];
                    if (v === undefined || v === null || v === "") continue;
                    const norm = typeof v === "object" ? v?.id ?? v : v;
                    if (norm !== undefined && norm !== null && norm !== "") {
                        next[k] = norm;
                        filled += 1;
                    }
                }
                if (filled > 0) {
                    toast.success(
                        `${filled} alan otomatik dolduruldu.`,
                    );
                }
                return next;
            });
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Otomatik doldurma başarısız.",
            );
        } finally {
            setAutoFilling(false);
        }
    }

    function handleFieldChange(fld, v, meta) {
        setValues((s) => {
            const next = { ...s, [fld.name]: v };
            if (fld.name === "kimlik_tipi") {
                const tip = v || "TC";
                if (next.tc_kimlik_no) {
                    next.tc_kimlik_no = normalizeKimlikNo(next.tc_kimlik_no, tip);
                }
            }
            if (fld.kimlikLinked) {
                const tip = next.kimlik_tipi || s.kimlik_tipi || "TC";
                next[fld.name] = normalizeKimlikNo(v, tip);
            }
            if (fld.type === "fk" && fld.fkSubmit === "label") {
                if (meta && "label" in meta) {
                    next[`${fld.name}__label`] = meta.label || "";
                }
            }
            return next;
        });
        if (fld.type === "fk" && fld.autoFillFrom) {
            handleAutoFill(fld, v);
        }
    }

    /* ------- Submit ------- */
    function askWorkflowBypass(errors) {
        const lines = (errors || []).filter(Boolean);
        if (!lines.length) return Promise.resolve(false);
        return new Promise((resolve) => {
            workflowConfirmRef.current = resolve;
            setWorkflowConfirmLines(lines);
            setWorkflowConfirmOpen(true);
        });
    }

    function closeWorkflowConfirm(confirmed) {
        setWorkflowConfirmOpen(false);
        setWorkflowConfirmLines([]);
        const resolve = workflowConfirmRef.current;
        workflowConfirmRef.current = null;
        resolve?.(confirmed);
    }

    async function submitPayload(payload, force = false) {
        setSubmitting(true);
        setErr(null);
        try {
            await onSubmit(payload, { force });
        } catch (e) {
            const data = e?.response?.data;
            let msg = e?.message || "Bir hata oluştu.";
            if (data && typeof data === "object") {
                const wf = data.workflow;
                if (wf?.errors?.length) {
                    if (!force && (await askWorkflowBypass(wf.errors))) {
                        await submitPayload(payload, true);
                        return;
                    }
                    msg = wf.errors.join("\n");
                } else if (typeof data.detail === "string" && data.detail) {
                    msg = data.detail;
                } else {
                    msg = Object.entries(data)
                        .filter(([k]) => k !== "workflow")
                        .map(([k, v]) => {
                            if (v && typeof v === "object" && !Array.isArray(v)) {
                                return `${humanizeKey(k)}: ${JSON.stringify(v)}`;
                            }
                            return `${humanizeKey(k)}: ${
                                Array.isArray(v) ? v.join(", ") : String(v)
                            }`;
                        })
                        .join("\n");
                }
            }
            setErr(msg);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmit(e) {
        if (e?.preventDefault) e.preventDefault();
        setErr(null);

        const missingRequired = allFields.filter((fld) => {
            if (!fld.required || fld.readOnly || fld.hidden) return false;
            const v = values[fld.name];
            if (fld.type === "boolean") return false;
            if (fld.type === "fk") {
                if (isMultiFkField(fld)) {
                    return !Array.isArray(v) || v.length === 0;
                }
                return v === null || v === undefined || v === "";
            }
            return v === null || v === undefined || v === "";
        });
        if (missingRequired.length > 0) {
            setErr(
                missingRequired
                    .map((f) => `${f.label}: Bu alan zorunlu.`)
                    .join("\n"),
            );
            return;
        }

        const payload = {};
        for (const fld of allFields) {
            const v = values[fld.name];
            if (fld.readOnly || isReadOnlyName(fld.name)) continue;
            if (
                isEdit &&
                !isMultiFkField(fld) &&
                sameFieldValue(
                    fld,
                    v,
                    normalizeInitialField(fld, initial),
                    values,
                    initial,
                )
            ) {
                continue;
            }
            if (fld.type === "boolean") {
                payload[fld.name] = !!v;
            } else if (fld.type === "fk") {
                if (fld.fkSubmit === "label") {
                    const lbl = resolveFkLabelValue(fld, values);
                    if (v === null || v === undefined || v === "") {
                        payload[fld.name] = lbl || null;
                    } else if (lbl) {
                        payload[fld.name] = lbl;
                    } else if (typeof v === "string" && !/^\d+$/.test(v)) {
                        payload[fld.name] = v.trim();
                    } else {
                        setErr(
                            `${fld.label}: Seçim kaydedilemedi — lütfen listeden tekrar seçin.`,
                        );
                        return;
                    }
                    continue;
                }
                if (isMultiFkField(fld)) {
                    payload[fld.name] = aracilarToIdList(
                        v === null || v === undefined
                            ? normalizeInitialField(fld, initial)
                            : v,
                    );
                } else if (v === null || v === undefined || v === "") {
                    if (!isEdit) continue;
                    if (!fld.required) payload[fld.name] = null;
                } else if (Array.isArray(v)) {
                    payload[fld.name] =
                        v
                            .map((x) =>
                                typeof x === "object" ? x?.id ?? null : x,
                            )
                            .filter((x) => x !== null && x !== undefined)[0] ??
                        null;
                } else {
                    payload[fld.name] =
                        typeof v === "object" ? v.id ?? null : v;
                }
            } else if (fld.type === "number") {
                if (v === "" || v === null || v === undefined) continue;
                const n = Number(v);
                if (!Number.isNaN(n)) payload[fld.name] = n;
            } else if (fld.type === "select") {
                if (v === "" || v === null || v === undefined) {
                    if (!isEdit && fld.required) continue;
                    if (isEdit && !fld.required) payload[fld.name] = null;
                } else if (fld.numericSubmit) {
                    const n = Number(v);
                    payload[fld.name] = Number.isNaN(n) ? v : n;
                } else {
                    payload[fld.name] = v;
                }
            } else if (v === "" || v === null || v === undefined) {
                if (!isEdit) continue;
                if (!fld.required) payload[fld.name] = null;
            } else {
                payload[fld.name] = v;
            }
        }

        if (isEdit && Object.keys(payload).length === 0) {
            toast.info("Kaydedilecek değişiklik yok.");
            return;
        }

        let forceSubmit = false;
        const statusChanged =
            isEdit &&
            resource === "sigorta-dosyalari" &&
            initial?.id &&
            payload.dosya_durumu &&
            String(payload.dosya_durumu) !== String(initial.dosya_durumu);

        if (statusChanged) {
            try {
                const wf = await workflowValidate({
                    dosya_id: initial.id,
                    new_status: payload.dosya_durumu,
                    old_status: initial.dosya_durumu,
                });
                if (!wf?.ok && wf?.errors?.length) {
                    if (!(await askWorkflowBypass(wf.errors))) return;
                    forceSubmit = true;
                }
            } catch {
                /* doğrulama servisi yanıt vermezse normal kayda devam */
            }
        }

        await submitPayload(payload, forceSubmit);
    }

    const modal = (
        <div
            data-testid="entity-form-modal"
            className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="fixed inset-0 bg-black/70 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative min-h-full flex items-start justify-center px-4 py-6 sm:py-10">
            <div className="relative w-full max-w-4xl max-h-[min(88vh,900px)] bg-[#121A2F] border border-[#D4AF37]/25 rounded-xl shadow-2xl shadow-black/50 flex flex-col animate-fade-in my-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
                            {isEdit ? "Düzenle" : "Yeni Kayıt"}
                            {schemaLoading && (
                                <span className="inline-flex items-center gap-1 text-[#D4AF37] normal-case tracking-normal">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    schema yükleniyor
                                </span>
                            )}
                            {!formFieldsStrict &&
                                schemaFields &&
                                schemaFields.length > 0 && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[9px] tracking-wider normal-case">
                                        DYNAMIC ({schemaFields.length})
                                    </span>
                                )}
                        </div>
                        <h2 className="font-display text-2xl text-white">
                            {title}
                        </h2>
                    </div>
                    <button
                        data-testid="entity-form-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-md hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="flex-1 overflow-y-auto px-6 py-5"
                >
                    {schemaError && (
                        <div className="mb-4 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>
                                Schema yüklenemedi: {schemaError}. Form yine de
                                çalışır — declared/discovered alanlar kullanılır.
                            </div>
                        </div>
                    )}

                    {err && (
                        <div className="mb-4 px-3 py-2 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 text-xs flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            <pre className="whitespace-pre-wrap font-sans">
                                {err}
                            </pre>
                        </div>
                    )}

                    {resource === "sigorta-dosyalari" && isEdit && initial?.id ? (
                        <div className="mb-6">
                            <DosyaIntelPanel dosyaId={initial.id} compact />
                        </div>
                    ) : null}

                    {loading ? (
                        <div className="py-12 flex flex-col items-center text-slate-500">
                            <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37] mb-3" />
                            <div className="text-sm">Kayıt yükleniyor…</div>
                        </div>
                    ) : renderedGroups ? (
                        <div className="space-y-6">
                            {renderedGroups.map((g) => (
                                <fieldset
                                    key={g.title}
                                    className="rounded-lg border border-white/10 bg-[#0B0F19]/40 px-4 py-4"
                                >
                                    <legend className="px-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">
                                        {g.title}
                                    </legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {g.fields.map((fld) => (
                                            <FieldInput
                                                key={fld.name}
                                                field={fieldForRender(fld, values)}
                                                value={values[fld.name]}
                                                onChange={(v, meta) =>
                                                    handleFieldChange(fld, v, meta)
                                                }
                                                labelHint={fkLabelHint(
                                                    initial,
                                                    fld.name,
                                                )}
                                                busy={
                                                    autoFilling &&
                                                    fld.type === "fk" &&
                                                    !!fld.autoFillFrom
                                                }
                                                resource={resource}
                                                onRayicLookup={handleRayicLookup}
                                                rayicLoading={rayicLoading}
                                            />
                                        ))}
                                    </div>
                                </fieldset>
                            ))}
                            {ungroupedFields.length > 0 && (
                                <fieldset className="rounded-lg border border-white/10 bg-[#0B0F19]/40 px-4 py-4">
                                    <legend className="px-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Diğer Alanlar
                                    </legend>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {ungroupedFields.map((fld) => (
                                            <FieldInput
                                                key={fld.name}
                                                field={fieldForRender(fld, values)}
                                                value={values[fld.name]}
                                                onChange={(v, meta) =>
                                                    handleFieldChange(fld, v, meta)
                                                }
                                                labelHint={fkLabelHint(
                                                    initial,
                                                    fld.name,
                                                )}
                                                busy={
                                                    autoFilling &&
                                                    fld.type === "fk" &&
                                                    !!fld.autoFillFrom
                                                }
                                                resource={resource}
                                                onRayicLookup={handleRayicLookup}
                                                rayicLoading={rayicLoading}
                                            />
                                        ))}
                                    </div>
                                </fieldset>
                            )}
                            {extraSection && (
                                <div className="w-full">{extraSection}</div>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {allFields.map((fld) => (
                                <FieldInput
                                    key={fld.name}
                                    field={fieldForRender(fld, values)}
                                    value={values[fld.name]}
                                    onChange={(v, meta) =>
                                        handleFieldChange(fld, v, meta)
                                    }
                                    labelHint={fkLabelHint(initial, fld.name)}
                                    busy={
                                        autoFilling &&
                                        fld.type === "fk" &&
                                        !!fld.autoFillFrom
                                    }
                                    resource={resource}
                                    onRayicLookup={handleRayicLookup}
                                    rayicLoading={rayicLoading}
                                />
                            ))}
                            {extraSection}
                        </div>
                    )}

                    {schemaLoading && allFields.length === 0 && (
                        <div className="py-12 flex flex-col items-center text-slate-500">
                            <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37] mb-3" />
                            <div className="text-sm">
                                Form alanları yükleniyor…
                            </div>
                        </div>
                    )}
                </form>

                <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3 bg-[#0B0F19]/40">
                    <div className="text-[10px] text-slate-600">
                        {allFields.length > 0 && (
                            <span>
                                {allFields.length} alan
                                {schemaFields && schemaFields.length > 0 && (
                                    <span className="text-slate-500">
                                        {" "}
                                        · {schemaFields.length} backend'den
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="entity-form-cancel"
                            onClick={onClose}
                            className="h-10 px-4 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            data-testid="entity-form-submit"
                            onClick={handleSubmit}
                            disabled={submitting || loading}
                            className="btn-gold h-10 px-5 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting ? (
                                <div className="h-4 w-4 rounded-full border-2 border-black/40 border-t-transparent animate-spin" />
                            ) : (
                                <Save className="h-4 w-4" />
                            )}
                            {isEdit ? "Güncelle" : "Kaydet"}
                        </button>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );

    return createPortal(
        <>
            {modal}
            <ConfirmDialog
                open={workflowConfirmOpen}
                title="Dosya durumu uyarısı"
                subtitle="Eksik evrak veya kapanış koşulu var."
                tone="warning"
                confirmLabel="Yine de kaydet"
                message={[
                    "Aşağıdaki koşullar sağlanmadı:",
                    "",
                    ...workflowConfirmLines.map((line) => `• ${line}`),
                    "",
                    "Yine de bu değişiklikleri kaydetmek istiyor musunuz?",
                ].join("\n")}
                onConfirm={() => closeWorkflowConfirm(true)}
                onCancel={() => closeWorkflowConfirm(false)}
            />
        </>,
        document.body,
    );
}

/* =================================================================
 *  FIELD INPUT
 * ================================================================= */

function FieldInput({ field, value, onChange, busy = false, labelHint = "", resource = "", onRayicLookup = null, rayicLoading = false }) {
    const baseInput =
        "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition";

    const isFullWidth =
        field.type === "textarea" || field.fullWidth === true;

    return (
        <div className={isFullWidth ? "md:col-span-2" : ""}>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1.5 flex items-center gap-1.5">
                {field.autoFillFrom && (
                    <Sparkles className="h-3 w-3 text-[#D4AF37]" />
                )}
                {field.label}
                {field.required && (
                    <span className="text-[#D4AF37] ml-1">*</span>
                )}
                {busy && (
                    <span className="ml-2 text-[10px] text-[#F3E5AB] normal-case tracking-normal">
                        Yükleniyor…
                    </span>
                )}
            </label>
            {field.type === "textarea" ? (
                <textarea
                    data-testid={`field-${field.name}`}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={field.rows || 4}
                    required={field.required}
                    readOnly={field.readOnly}
                    placeholder={field.placeholder || ""}
                    maxLength={field.maxLength}
                    className={
                        baseInput +
                        " resize-y min-h-[100px] " +
                        (field.mono ? "font-mono-jb text-[12px] leading-relaxed" : "")
                    }
                />
            ) : field.name === "aracilar" ? (
                <AracilarMultiField
                    testId={`field-${field.name}`}
                    value={value}
                    onChange={(ids) => onChange(ids)}
                    placeholder={
                        field.placeholder || "Birden fazla aracı seçebilirsiniz"
                    }
                />
            ) : field.type === "fk" ? (
                <SearchableSelect
                    testId={`field-${field.name}`}
                    value={resolveFkSelectValue(value, labelHint, {
                        multi: isMultiFkField(field),
                    })}
                    onChange={(id, meta) => onChange(id, meta)}
                    resource={field.resource}
                    placeholder={field.placeholder || "Seçiniz..."}
                    required={field.required}
                    multi={isMultiFkField(field)}
                    labelHint={isMultiFkField(field) ? "" : labelHint}
                />
            ) : field.type === "select" ? (
                <select
                    data-testid={`field-${field.name}`}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    required={field.required}
                    className={baseInput}
                >
                    <option value="">Seçiniz...</option>
                    {(field.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                    {value &&
                        !(field.options || []).some(
                            (o) => String(o.value) === String(value),
                        ) && (
                            <option value={value}>
                                {labelHint || String(value)}
                            </option>
                        )}
                </select>
            ) : field.type === "boolean" ? (
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                        data-testid={`field-${field.name}`}
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        className="h-4 w-4 rounded border-white/20 bg-[#0B0F19] accent-[#D4AF37]"
                    />
                    <span className="text-sm text-slate-300">Evet</span>
                </label>
            ) : field.type === "date" ? (
                <input
                    data-testid={`field-${field.name}`}
                    type="date"
                    value={(value || "").slice(0, 10)}
                    onChange={(e) => onChange(e.target.value)}
                    required={field.required}
                    className={baseInput}
                />
            ) : field.type === "datetime" ? (
                <input
                    data-testid={`field-${field.name}`}
                    type="datetime-local"
                    value={
                        value
                            ? String(value).replace("Z", "").slice(0, 16)
                            : ""
                    }
                    onChange={(e) => onChange(e.target.value)}
                    required={field.required}
                    className={baseInput}
                />
            ) : field.type === "number" ? (
                field.name === "arac_rayic_degeri" && resource === "sigorta-dosyalari" ? (
                    <div className="flex gap-2">
                        <input
                            data-testid={`field-${field.name}`}
                            type="number"
                            value={value ?? ""}
                            onChange={(e) => onChange(e.target.value)}
                            required={field.required}
                            step="any"
                            className={baseInput}
                        />
                        <button
                            type="button"
                            onClick={onRayicLookup}
                            disabled={rayicLoading}
                            className="px-3 py-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/40 border border-[#D4AF37]/40 hover:from-[#D4AF37]/30 hover:to-[#D4AF37]/50 text-[#F3E5AB] text-xs font-semibold rounded-md flex items-center gap-1.5 transition shrink-0 disabled:opacity-50"
                            title="Yapay Zeka Destekli Rayiç Sorgulama"
                        >
                            {rayicLoading ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
                            )}
                            Rayiç Bul
                        </button>
                    </div>
                ) : (
                    <input
                        data-testid={`field-${field.name}`}
                        type="number"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        required={field.required}
                        step="any"
                        className={baseInput}
                    />
                )
            ) : (
                <input
                    data-testid={`field-${field.name}`}
                    type={
                        field.type === "email"
                            ? "email"
                            : field.type === "phone"
                              ? "tel"
                              : field.type === "url"
                                ? "url"
                                : "text"
                    }
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    required={field.required}
                    readOnly={field.readOnly}
                    maxLength={field.maxLength}
                    placeholder={field.placeholder || ""}
                    className={baseInput}
                />
            )}
            {field.helpText ? (
                <div className="text-[10px] text-slate-600 mt-1">{field.helpText}</div>
            ) : null}
        </div>
    );
}
